import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import { breakpoints, Fonts } from '@styles/index';
import {
  SPACING_3B,
  SPACING_10B,
  SPACING_19B,
  SPACING_4B,
  SPACING_6B,
  SPACING_2B,
  SPACING_8B,
  SPACING_16B,
  SPACING_5B
} from '@styles/constants/spacings';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getSourcesFromUrlMap } from '@utils/resourceHelper';
import Button from '@base/Button/Button';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import { AAD_TAB_CLICKED } from '@constants/segment';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from '@selectors/auth';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useGoalBenefits from '@api/hooks/goal/useGoalBenefits';
import {
  ONBOARDING_WATCH_NOW_CLICKED,
  ONBOARDING_GET_THE_APP_CLICKED
} from '@cont/Onboarding/constants';
import { useIsMobile } from '@stores/AppCommonData';

const Wrapper = styled.div`
  width: 100%;
  max-width: 1136px;
  margin: ${SPACING_19B} auto 160px auto;
  @media (max-width: ${breakpoints.tablet}) {
    max-width: 728px;
    margin: ${SPACING_10B} auto ${SPACING_16B} auto;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: -53px;
  }
`;

const CardsContainer = styled.div`
  margin-top: ${SPACING_10B};
  display: grid;
  grid-template: 1fr / 1fr 1fr;
  grid-column-gap: ${SPACING_10B};
  @media (max-width: ${breakpoints.tablet}) {
    margin-top: ${SPACING_8B};
    grid-column-gap: ${SPACING_4B};
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 0;
    grid-template: 1fr 1fr / 1fr;
    grid-row-gap: ${SPACING_6B};
    padding: 0 ${SPACING_4B};
  }
`;

const Card = styled.div`
  height: 409px;
  padding: ${SPACING_10B};
  background-color: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${SPACING_3B};
  display: flex;
  flex-direction: column;
  position: relative;
  @media (max-width: ${breakpoints.tablet}) {
    height: 293px;
    padding: ${SPACING_6B};
  }
  @media (max-width: ${breakpoints.mobile}) {
    padding: ${SPACING_6B} ${SPACING_4B};
  }
`;

const CardTitle = styled(H2)`
  margin-bottom: ${SPACING_2B};
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 22px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${Fonts.H3};
  }
`;

const Feature = styled.div`
  display: grid;
  grid-template: 1fr / max-content 1fr;
  grid-column-gap: ${SPACING_4B};
  align-items: center;
  margin-top: ${SPACING_2B};
  position: relative;
  @media (max-width: ${breakpoints.mobile}) {
    grid-column-gap: ${SPACING_2B};
  }
`;

const FeatureIcon = styled.picture`
  img {
    width: 36px;
    height: 36px;
  }
  margin-bottom: -3px;
  @media (max-width: ${breakpoints.tablet}) {
    img {
      width: 30px;
      height: 30px;
    }
  }
`;

const FeatureTitle = styled(H5)`
  font-weight: ${Fonts.REGULAR};
  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${Fonts.H6};
  }
`;

const BgImage = styled.picture`
  position: absolute;
  bottom: 0;
  right: 0;
  margin-bottom: -3px;
  img {
    @media (max-width: ${breakpoints.tablet}) {
      width: 130px;
    }
  }
`;

const StyledButton = styled(Button)`
  margin-top: auto;
  padding: ${SPACING_4B} ${SPACING_8B};
  width: fit-content;
  @media (max-width: ${breakpoints.tablet}) {
    padding: ${SPACING_3B} ${SPACING_5B};
    font-size: ${Fonts.H6};
  }
  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${Fonts.H6};
    padding: ${SPACING_4B} 30px;
  }
`;

const keyEventNameMap = {
  free_live_classes: ONBOARDING_WATCH_NOW_CLICKED,
  solve_your_doubts: ONBOARDING_GET_THE_APP_CLICKED
};

const CardComponent = ({
  goalUID,
  goalSlug,
  benefit,
  isCatalogueFeatureAvailable
}) => {
  const cta = benefit.cta || {};
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isMobile = useIsMobile();

  const showTextbookSolutions = () => {
    return (
      isCatalogueFeatureAvailable &&
      benefit.key === 'solve_your_doubts' &&
      !isMobile
    );
  };

  const handleEvents = (key) => {
    if (!key) return;

    let eventName;
    let customProperty;
    if (showTextbookSolutions()) {
      eventName = AAD_TAB_CLICKED;
      customProperty = {
        is_subscription_active: false,
        last_primary_source_section: 'Banner Clicked'
      };
    } else {
      eventName = keyEventNameMap[key];
      customProperty = { is_logged_in: isLoggedIn };
    }

    if (eventName) segment.track(eventName, customProperty);
  };

  const onCTAClick = () => {
    const key = (benefit.key || '').toLowerCase();
    handleEvents(key);

    if (showTextbookSolutions()) {
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/doubts-and-solutions',
        `/goal/${goalSlug}/${goalUID}/doubts-and-solutions`
      );
    } else if (cta.target === '_blank') {
      window.open(cta.href, cta.target);
    } else if (key === 'free_live_classes') {
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/classes',
        `/goal/${goalSlug}/${goalUID}/classes`
      );
    }
  };
  return (
    <Card key={benefit.title}>
      <BgImage>{getSourcesFromUrlMap(benefit.bg, true)}</BgImage>
      <CardTitle>{benefit.title}</CardTitle>
      {(benefit.features || []).map((feature) => (
        <Feature key={feature.title}>
          <FeatureIcon>{getSourcesFromUrlMap(feature.urls, true)}</FeatureIcon>
          <FeatureTitle>{feature.title}</FeatureTitle>
        </Feature>
      ))}
      <StyledButton label={cta.label} onClick={onCTAClick} />
    </Card>
  );
};

CardComponent.propTypes = {
  benefit: PropTypes.objectOf(PropTypes.any).isRequired,
  isCatalogueFeatureAvailable: PropTypes.bool
};

CardComponent.defaultProps = {
  isCatalogueFeatureAvailable: false
};

const GoalBenefits = ({ goalUID, goalSlug }) => {
  const { goalInfo } = useGoalInfo(goalUID);
  const isCatalogueFeatureEnabled = goalInfo?.isCatalogueFeatureAvailable;
  const goalBenefitKey = isCatalogueFeatureEnabled
    ? 'doubts_and_solutions'
    : goalUID;
  const { goalBenefits: { benefits = [] } = {} } =
    useGoalBenefits(goalBenefitKey);
  return (
    <Wrapper>
      <CardsContainer>
        {benefits.map((benefit) => (
          <CardComponent
            key={benefit.title}
            benefit={benefit}
            isCatalogueFeatureAvailable={isCatalogueFeatureEnabled}
            goalUID={goalUID}
            goalSlug={goalSlug}
          />
        ))}
      </CardsContainer>
    </Wrapper>
  );
};

export default GoalBenefits;
